/**
 * Video timecodes (video ingest plan §6.1): the processed markdown marks every
 * paragraph with `[mm:ss]`, from one hour on with `[h:mm:ss]`. The same format
 * labels chat sources ("Video · 12:34") and becomes a jump link in answers.
 */

/** 27 → "00:27", 754 → "12:34", 3723 → "1:02:03". */
export function formatTimecode(seconds: number): string {
  const total = Number.isFinite(seconds) && seconds > 0 ? Math.floor(seconds) : 0
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  const ss = String(s).padStart(2, '0')
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${ss}`
  return `${String(m).padStart(2, '0')}:${ss}`
}

const TIMECODE_RE = /^(?:(\d{1,2}):)?([0-5]?\d):([0-5]\d)$/

/** "12:34" → 754, "1:02:03" → 3723; anything else → null. */
export function parseTimecode(text: string): number | null {
  const match = TIMECODE_RE.exec(text.trim())
  if (!match) return null
  const [, h, m, s] = match
  return (h ? Number(h) * 3600 : 0) + Number(m) * 60 + Number(s)
}

/** Link target a linkified timecode carries; never a real URL. */
export const TIMECODE_HREF_PREFIX = '#cdb-video-t='

export function parseTimecodeHref(href: string | undefined): number | null {
  if (!href?.startsWith(TIMECODE_HREF_PREFIX)) return null
  const value = Number(href.slice(TIMECODE_HREF_PREFIX.length))
  return Number.isFinite(value) && value >= 0 ? value : null
}

// `[mm:ss]` or `[h:mm:ss]`, optionally a range `[00:00–05:00]` (the first time
// counts), not already a link target (`](`) or link text (`[..](..)`).
const INLINE_TIMECODE_RE = /(?<![\\\]])\[((?:\d{1,2}:)?[0-5]?\d:[0-5]\d)(?:\s*[–-]\s*(?:\d{1,2}:)?[0-5]?\d:[0-5]\d)?\](?!\()/g

/**
 * Turn the timecodes of a markdown text into links a renderer can recognise
 * (`parseTimecodeHref`). Fenced and inline code stays untouched.
 */
export function linkifyTimecodes(markdown: string): string {
  if (!markdown.includes(':')) return markdown
  // Split off code: odd segments are ``` fences or `inline` code.
  const parts = markdown.split(/(```[\s\S]*?```|`[^`\n]*`)/)
  return parts
    .map((part, i) => {
      if (i % 2 === 1) return part
      return part.replace(INLINE_TIMECODE_RE, (whole, first: string) => {
        const seconds = parseTimecode(first)
        return seconds === null ? whole : `[${whole.slice(1, -1)}](${TIMECODE_HREF_PREFIX}${seconds})`
      })
    })
    .join('')
}
